import type { FlagPriority, FlagTypes } from '@generated/graphql';
import { Box, RefBox } from '@ovComponents/1-primative/box/box';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import {
  ConfirmationModal,
  ConfirmButtonColors,
} from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlagPolicyRowView } from './flagPolicies.types';
import {
  EDITABLE_FLAG_PRIORITIES,
  formatThresholdCentsAsDollars,
  getThresholdAxis,
  parseThresholdDollarsToCents,
} from './utils/flagPolicy';

type FlagPolicyConfigFormProps = {
  row: FlagPolicyRowView;
  readOnly: boolean;
  disabled?: boolean;
  onPriorityChange: (flagType: FlagTypes, priority: FlagPriority) => void;
  onThresholdCentsChange: (flagType: FlagTypes, value: number | null) => void;
  onPercentThresholdChange: (flagType: FlagTypes, value: number | null) => void;
  testIdPrefix?: string;
};

export const FlagPolicyConfigForm = ({
  row,
  readOnly,
  disabled = false,
  onPriorityChange,
  onThresholdCentsChange,
  onPercentThresholdChange,
  testIdPrefix,
}: FlagPolicyConfigFormProps) => {
  const { t } = useTranslation(['settings', 'shared', 'reconciliation']);
  const thresholdAxis = getThresholdAxis(row.flagType);
  const isDisabled = disabled || readOnly;

  const priority = row.priority;
  const thresholdCents = row.thresholdCents;
  const percentThreshold = row.percentThreshold;

  const [priorityConfirmOpen, setPriorityConfirmOpen] = useState(false);
  const [pendingPriority, setPendingPriority] = useState<FlagPriority | null>(
    null,
  );
  const [thresholdDollarsDraft, setThresholdDollarsDraft] = useState(() =>
    thresholdCents != null ? formatThresholdCentsAsDollars(thresholdCents) : '',
  );
  const isThresholdDollarsFocusedRef = useRef(false);
  const thresholdInputRef = useRef<HTMLDivElement>(null);
  const thresholdDollarsDraftRef = useRef(thresholdDollarsDraft);
  thresholdDollarsDraftRef.current = thresholdDollarsDraft;

  useEffect(() => {
    if (isThresholdDollarsFocusedRef.current) {
      return;
    }
    setThresholdDollarsDraft(
      thresholdCents != null
        ? formatThresholdCentsAsDollars(thresholdCents)
        : '',
    );
  }, [row.flagType, thresholdCents]);

  const commitThresholdDollars = useCallback(
    (raw: string): void => {
      const trimmed = raw.trim();
      if (trimmed === '') {
        onThresholdCentsChange(row.flagType, null);
        setThresholdDollarsDraft('');
        return;
      }
      const cents = parseThresholdDollarsToCents(trimmed);
      if (cents == null) {
        setThresholdDollarsDraft(
          thresholdCents != null
            ? formatThresholdCentsAsDollars(thresholdCents)
            : '',
        );
        return;
      }
      onThresholdCentsChange(row.flagType, cents);
      setThresholdDollarsDraft(formatThresholdCentsAsDollars(cents));
    },
    [onThresholdCentsChange, row.flagType, thresholdCents],
  );

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent): void => {
      if (!isThresholdDollarsFocusedRef.current) {
        return;
      }
      if (thresholdInputRef.current?.contains(event.target as Node)) {
        return;
      }
      isThresholdDollarsFocusedRef.current = false;
      commitThresholdDollars(thresholdDollarsDraftRef.current);
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [commitThresholdDollars]);

  const handleThresholdDollarsChange = (value: string): void => {
    setThresholdDollarsDraft(value);
  };

  const handlePrioritySelect = (
    event: ChangeEvent<{ value: unknown }>,
  ): void => {
    const nextPriority = event.target.value as FlagPriority;
    if (nextPriority === priority) {
      return;
    }
    setPendingPriority(nextPriority);
    setPriorityConfirmOpen(true);
  };

  const handleConfirmPriorityChange = (): void => {
    if (pendingPriority != null) {
      onPriorityChange(row.flagType, pendingPriority);
    }
    setPriorityConfirmOpen(false);
    setPendingPriority(null);
  };

  const handleCancelPriorityChange = (): void => {
    setPriorityConfirmOpen(false);
    setPendingPriority(null);
  };

  return (
    <Box
      display='flex'
      flexDirection='column'
      gap={1.5}
      mt={1.5}
      p={1.5}
      sx={{
        borderRadius: 1,
        backgroundColor: readOnly ? 'action.hover' : 'transparent',
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <SelectField
        label={t('flagPolicies.config.priority')}
        value={priority}
        onChange={handlePrioritySelect}
        disabled={isDisabled}
        size='small'
        testId={`${testIdPrefix}-priority`}
        fullWidth
      >
        {EDITABLE_FLAG_PRIORITIES.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`reconciliation:reconFlags.severity.${value.toLowerCase()}`)}
          </MenuItem>
        ))}
      </SelectField>

      {thresholdAxis === 'CENTS' && (
        <RefBox ref={thresholdInputRef}>
          <NumberField
            label={t('flagPolicies.config.thresholdDollars')}
            number={thresholdDollarsDraft}
            setNumber={handleThresholdDollarsChange}
            onFocus={() => {
              isThresholdDollarsFocusedRef.current = true;
            }}
            onKeyDown={(event) => {
              if (event.key === 'Tab' || event.key === 'Enter') {
                isThresholdDollarsFocusedRef.current = false;
                commitThresholdDollars(thresholdDollarsDraftRef.current);
              }
            }}
            decimalPlaces={2}
            min={0}
            unsigned
            disabled={isDisabled}
            testId={`${testIdPrefix}-threshold-dollars`}
            size='small'
            fullWidth
          />
        </RefBox>
      )}

      {thresholdAxis === 'PERCENT_ONLY' && (
        <NumberField
          label={t('flagPolicies.config.thresholdPercent')}
          number={percentThreshold != null ? String(percentThreshold) : ''}
          setNumber={(value) => {
            const parsed = value.trim();
            onPercentThresholdChange(
              row.flagType,
              parsed === '' ? null : Number(parsed),
            );
          }}
          decimalPlaces={2}
          min={0}
          max={100}
          trailingIcon='percent'
          unsigned
          disabled={isDisabled}
          testId={`${testIdPrefix}-threshold-percent`}
          size='small'
          fullWidth
        />
      )}
      <ConfirmationModal
        open={priorityConfirmOpen}
        title={t('flagPolicies.priorityChangeConfirm.title')}
        bodyText={t('flagPolicies.priorityChangeConfirm.body')}
        confirmButtonLabel={t('shared:confirm')}
        cancelButtonLabel={t('shared:cancel')}
        confirmButtonColor={ConfirmButtonColors.primary}
        onConfirm={handleConfirmPriorityChange}
        onCancel={handleCancelPriorityChange}
        testIdPrefix={`${testIdPrefix ?? 'flag-policy'}-priority-change`}
      />
    </Box>
  );
};
